// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import setRequestQuoteModal from "@flip/App/Handlers/RequestQuoteModal/RequestQuoteModalHandler";
import { addToCartStatus } from "@flip/XDL/constants";
import { color, mergeWithCss, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    HasHistory;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        // XNG-Changes
        customRequestQuoteUrl: getPageLinkByPageType(state, "RequestQuotePage")?.url,
        canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    setRequestQuoteModal,
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {
    sizeVariant: "medium",
    mergeCss: true,
    css: css`
        position: relative;
        color: ${color("text")};

        &.loading,
        &.success {
            color: transparent;
        }

        &.success > ${TypographyStyle} {
            color: ${color("common.ui1")};
            text-align: center;
        }
        & .center {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%);
        }
    `,
};

// XNG-Changes
const dotsStyleColor = "common.ui1";
const dotsStyle = {
    css: css`
        position: relative;
        left: -9999px;
        width: 3px;
        height: 3px;
        border-radius: 5px;
        background-color: ${color(dotsStyleColor)};
        color: ${color(dotsStyleColor)};
        box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
        animation: dotPulse 1.5s infinite linear;
        animation-delay: 0.25s;

        &::before,
        &::after {
            content: "";
            display: inline-block;
            position: absolute;
            top: 0;
            width: 3px;
            height: 3px;
            border-radius: 5px;
            background-color: ${color(dotsStyleColor)};
            color: ${color(dotsStyleColor)};
        }

        &::before {
            box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            animation: dotPulseBefore 1.5s infinite linear;
            animation-delay: 0s;
        }

        &::after {
            box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            animation: dotPulseAfter 1.5s infinite linear;
            animation-delay: 0.5s;
        }

        @keyframes dotPulseBefore {
            0% {
                box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 9984px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 9984px 0 0 -5px ${color(dotsStyleColor)};
            }
        }

        @keyframes dotPulse {
            0% {
                box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 9999px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 9999px 0 0 -5px ${color(dotsStyleColor)};
            }
        }

        @keyframes dotPulseAfter {
            0% {
                box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            }
            30% {
                box-shadow: 10014px 0 0 2px ${color(dotsStyleColor)};
            }
            60%,
            100% {
                box-shadow: 10014px 0 0 -5px ${color(dotsStyleColor)};
            }
        }
    `,
};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        // XNG-Changes: add failedToLoadPricing
        productInfo: { qtyOrdered, inventory, unitOfMeasure, pricing, failedToLoadInventory, failedToLoadPricing },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    signInUrl,
    currentLocation,
    // XNG-Changes
    canSeePrices,
    productContext,
    customRequestQuoteUrl,
    setRequestQuoteModal,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeWithCss(productAddToCartButtonStyles, extendedStyles));
    const [status, setStatus] = useState(addToCartStatus.default);
    const history = useHistory();

    // XNG-Changes
    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    // XNG-Changes
    const showQuote =
        quoteRequired ||
        (!quoteRequired && !allowZeroPricing && pricing?.unitNetPrice === 0 && pricing?.unitRegularPrice === 0) ||
        failedToLoadPricing ||
        !canSeePrices ||
        (inventory && inventory.qtyOnHand <= 0);

    // XNG-Changes
    const requestQuoteClickHandler = async () => {
        setRequestQuoteModal({ modalIsOpen: true, productContext });
        await addToCartClickHandler();
    };
    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        // if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
        //     toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
        //     return;
        // }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        // XNG-Changes
        setStatus(addToCartStatus.loading);

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        // XNG-Changes
        setStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: addToCartPopupTimeout,
            });
        }
    };
    if (showQuote) {
        return (
            <Button
                {...styles}
                onClick={requestQuoteClickHandler}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                {...otherProps}
                aria-label="Request a Quote"
            >
                {translate("Request a Quote")}
            </Button>
        );
    }

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            className={`${status.status}`}
            {...otherProps}
            aria-label="add to cart button"
        >
            {status === addToCartStatus.loading && (
                <StyledWrapper className="center">
                    <StyledWrapper {...dotsStyle} />
                </StyledWrapper>
            )}
            {status === addToCartStatus.success && <Typography>{addToCartStatus.success.label}</Typography>}
            {status === addToCartStatus.default && (
                <Typography>{labelOverride ?? addToCartStatus.default.label}</Typography>
            )}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductAddToCartButton)));
